import React, { useMemo, useState } from "react";
import { Card, Tag, Progress, Segmented, Divider, Typography, Avatar } from "antd";
import { CarOutlined, UserOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

import OrderStatusList from "./OrderStatusList";
import PaymentBreakdown from "./PaymentBreakdown";
import {
  getRouteDayDelivery,
  computeDeliveryCounts,
} from "../../utils/routeMonitoring";

const { Text } = Typography;

const overallStateConfig = {
  "Sin iniciar": "default",
  "En ruta": "orange",
  Finalizado: "green",
};

// "Finalizado" once nothing is left pending or in progress; "En ruta" once
// the driver has started (something is en ruta or already in a terminal
// state); "Sin iniciar" while every order is still "Por entregar".
const getOverallState = (counts) => {
  if (counts.total === 0) return "Sin iniciar";
  if (counts.pending + counts.enRuta === 0) return "Finalizado";
  const terminalCount =
    counts.delivered + counts.partiallyDelivered + counts.notDelivered + counts.rescheduled;
  if (counts.enRuta > 0 || terminalCount > 0) return "En ruta";
  return "Sin iniciar";
};

const DriverRouteCard = ({ driver, orders, routeDay, summary, summaryLoading }) => {
  const [segment, setSegment] = useState("Pendientes");

  const { deliveryStatusByOrder, counts, buckets, currentOrders } = useMemo(() => {
    const statusByOrder = new Map();
    const buckets = { Pendientes: [], Entregados: [], "No entregados": [], Reagendados: [] };
    const current = [];

    orders.forEach((order) => {
      const delivery = getRouteDayDelivery(order, routeDay);
      if (!delivery) return;
      statusByOrder.set(order.order_id, delivery.delivery_status);
      switch (delivery.delivery_status) {
        case "Entregado":
        case "Parcialmente entregado":
          buckets.Entregados.push(order);
          break;
        case "No entregado":
          buckets["No entregados"].push(order);
          break;
        case "Reagendado":
          buckets.Reagendados.push(order);
          break;
        case "En ruta":
          current.push(order);
          break;
        default:
          buckets.Pendientes.push(order);
      }
    });

    return {
      deliveryStatusByOrder: statusByOrder,
      counts: computeDeliveryCounts(orders, routeDay),
      buckets,
      currentOrders: current,
    };
  }, [orders, routeDay]);

  const overallState = getOverallState(counts);
  const terminalCount =
    counts.delivered + counts.partiallyDelivered + counts.notDelivered + counts.rescheduled;
  const progressPercent = counts.total > 0 ? Math.round((terminalCount / counts.total) * 100) : 0;

  return (
    <Card
      title={
        <div className="flex items-center gap-2">
          <Avatar size="small" icon={<UserOutlined />} />
          <span>{driver.name}</span>
        </div>
      }
      extra={<Tag color={overallStateConfig[overallState]}>{overallState}</Tag>}
      className="h-full"
    >
      <Progress
        percent={progressPercent}
        format={() => `${terminalCount}/${counts.total}`}
      />

      <div className="mt-2 mb-2">
        {currentOrders.length > 0 ? (
          currentOrders.map((order) => (
            <Link
              key={order.order_id}
              to={`/orders/${order.order_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit" }}
              className="flex items-center gap-2 bg-orange-50 rounded-md px-2 py-1 mb-1"
            >
              <CarOutlined style={{ color: "#fa8c16" }} />
              <Text className="text-sm">
                Entregando ahora: <Text strong>#{order.daily_id}</Text> {order.name}
              </Text>
            </Link>
          ))
        ) : (
          <Text type="secondary" className="text-xs italic">
            Sin pedidos en curso
          </Text>
        )}
      </div>

      <Segmented
        block
        value={segment}
        onChange={setSegment}
        options={[
          { label: `Pendientes (${buckets.Pendientes.length})`, value: "Pendientes" },
          { label: `Entregados (${buckets.Entregados.length})`, value: "Entregados" },
          { label: `No entregados (${buckets["No entregados"].length})`, value: "No entregados" },
          { label: `Reagendados (${buckets.Reagendados.length})`, value: "Reagendados" },
        ]}
      />
      <div className="mt-2 max-h-64 overflow-y-auto">
        <OrderStatusList
          orders={buckets[segment]}
          deliveryStatusByOrder={deliveryStatusByOrder}
        />
      </div>

      <Divider style={{ margin: "12px 0" }} />

      <PaymentBreakdown
        recaudacion={summary?.recaudacion}
        total={summary?.total}
        loading={summaryLoading}
        size="small"
      />
    </Card>
  );
};

export default DriverRouteCard;
